/**
 * Input Sanitization Utilities
 *
 * Provides functions to sanitize user input and prevent XSS attacks.
 * All user-generated content should be sanitized before rendering.
 *
 * Uses DOMPurify for HTML sanitization - a production-grade library trusted by
 * Google, Microsoft, and Mozilla for XSS prevention.
 */

import type { Config } from 'dompurify';
import logger from '../services/loggerService';

/**
 * Sanitizes plain text by escaping HTML special characters.
 * Use this for text that will be inserted into HTML but should not contain any markup.
 *
 * @param text - The text to sanitize
 * @returns Sanitized text with HTML entities escaped
 */
export function sanitizeText(text: string): string {
  if (!text) return '';

  // SECURITY FIRST: Entity-aware sanitization prevents double-encoding corruption (Rule 07 & 12).
  // We use a negative lookahead to only encode '&' if it's not already part of a valid HTML entity.
  return text
    .replace(/&(?!(amp|lt|gt|quot|#x27);)/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

/**
 * Decodes HTML entities back to their original characters.
 * Useful for non-HTML contexts like inline styles, canvas text, or chart options.
 *
 * @param text - The text with HTML entities to decode
 * @returns Decoded text
 */
export function decodeHtmlEntities(text: string): string {
  if (!text) return '';

  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#39;/g, "'");
}

/**
 * Alias for sanitizeText to match backend API and likely import usage.
 */
export const sanitizeInput = sanitizeText;

/**
 * Recursively sanitizes an object or array by applying sanitizeText to all string values.
 * This is useful for sanitizing entire payloads before sending to the backend.
 *
 * @param obj - The object or array to sanitize
 * @returns A new object with sanitized string values
 */
export function sanitizeObject<T>(obj: T): T {
  if (typeof obj === 'string') {
    return sanitizeText(obj) as unknown as T;
  }

  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  // Skip Blob and File instances to prevent corruption during binary uploads
  if (obj instanceof Blob || (typeof File !== 'undefined' && obj instanceof File)) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeObject(item)) as unknown as T;
  }

  const result: Record<string, unknown> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = sanitizeObject((obj as Record<string, unknown>)[key]);
    }
  }
  return result as T;
}

/**
 * Sanitizes HTML content using DOMPurify to prevent XSS attacks.
 * Use this for rich text content that should allow basic formatting.
 *
 * DOMPurify is a battle-tested, production-grade library used by major tech companies.
 * It protects against:
 * - Script injection (including SVG-based attacks)
 * - Event handler injection (onclick, onerror, etc.)
 * - Data URI attacks
 * - DOM clobbering
 * - Mutation XSS (mXSS)
 *
 * @param html - The HTML to sanitize
 * @returns Promise resolving to sanitized HTML with only safe tags and attributes
 */
export async function sanitizeHtml(html: string): Promise<string> {
  if (!html) return '';

  try {
    const { default: DOMPurify } = await import('dompurify');

    // Configure DOMPurify with strict security settings
    const config: Config = {
      // Allow only safe HTML tags for basic formatting
      ALLOWED_TAGS: [
        'b',
        'i',
        'em',
        'strong',
        'u',
        'p',
        'br',
        'a',
        'ul',
        'ol',
        'li',
        'span',
        'div',
      ],
      // Allow only safe attributes
      ALLOWED_ATTR: ['href', 'title', 'target', 'class', 'id'],
      // Block all URI schemes except http/https/mailto
      ALLOWED_URI_REGEXP:
        /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
      // Preserve text content even if tags are removed
      KEEP_CONTENT: true,
      // Return sanitized string (not DOM nodes)
      RETURN_DOM: false,
      // Return string (not DOM fragment)
      RETURN_DOM_FRAGMENT: false,
      // Prevent DOM clobbering attacks
      SANITIZE_DOM: true,
      // Force body tags to be removed
      FORCE_BODY: false,
    };

    // Sanitize and return
    return DOMPurify.sanitize(html, config) as string;
  } catch (error) {
    logger.error('Failed to load DOMPurify', error, 'sanitizer.sanitizeHtml');
    return '';
  }
}

/**
 * Sanitizes a URL to prevent javascript: and data: URL attacks.
 * Use this before setting href attributes or window.location.
 *
 * @param url - The URL to sanitize
 * @returns Sanitized URL or empty string if invalid
 */
export function sanitizeUrl(url: string): string {
  if (!url) return '';

  const trimmed = url.trim().toLowerCase();

  // Block dangerous URL schemes
  const dangerousSchemes = ['javascript:', 'data:', 'vbscript:', 'file:'];
  if (dangerousSchemes.some((scheme) => trimmed.startsWith(scheme))) {
    logger.warn('Blocked potentially dangerous URL', { url });
    return '';
  }

  // Allow http, https, mailto, tel, and relative URLs
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('mailto:') ||
    trimmed.startsWith('tel:') ||
    trimmed.startsWith('/') ||
    trimmed.startsWith('./') ||
    trimmed.startsWith('../')
  ) {
    return url.trim();
  }

  // For other cases, prepend https://
  return `https://${url.trim()}`;
}

/**
 * Sanitizes a filename to prevent path traversal attacks.
 * Use this when working with user-provided filenames.
 *
 * @param filename - The filename to sanitize
 * @returns Sanitized filename
 */
export function sanitizeFilename(filename: string): string {
  if (!filename) return '';

  return (
    filename
      // Remove path separators
      .replace(/[\/\\]/g, '')
      // Remove null bytes
      .replace(/\0/g, '')
      // Remove leading dots (hidden files)
      .replace(/^\.+/, '')
      // Replace other dangerous characters with underscore
      .replace(/[<>:"|?*]/g, '_')
      .trim()
  );
}

/**
 * Validates and sanitizes an email address.
 * Returns null if the email is invalid.
 *
 * @param email - The email to validate and sanitize
 * @returns Sanitized email or null if invalid
 */
export function sanitizeEmail(email: string): string | null {
  if (!email) return null;

  const trimmed = email.trim().toLowerCase();

  // Basic email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(trimmed)) {
    return null;
  }

  return trimmed;
}

/**
 * Sanitizes a phone number by removing non-numeric characters.
 *
 * @param phone - The phone number to sanitize
 * @returns Sanitized phone number with only digits and +
 */
export function sanitizePhone(phone: string): string {
  if (!phone) return '';

  // Keep only digits and plus sign
  return phone.replace(/[^\d+]/g, '').trim();
}
/**
 * Sanitizes a date string by allowing only digits and common separators.
 * Use this for manual date inputs to prevent XSS while allowing standard formats.
 *
 * @param date - The date string to sanitize
 * @returns Sanitized date string containing only 0-9, /, -, and .
 */
export function sanitizeDateString(date: string): string {
  if (!date) return '';
  return date.replace(/[^0-9/.\-]/g, '').trim();
}

/**
 * Masks a sensitive identifier (like email or phone number) for privacy in administrative views.
 *
 * @param identifier - The string to mask (e.g., "test@example.com")
 * @returns Masked string (e.g., "te***@example.com")
 */
export function maskIdentifier(identifier: string): string {
  if (!identifier) return '';

  const trimmed = identifier.trim();

  // If it's an email, mask the middle of the local part
  if (trimmed.includes('@')) {
    const [local, domain] = trimmed.split('@');
    if (local.length <= 2) return `*@${domain}`;
    return `${local.substring(0, 2)}***@${domain}`;
  }

  // If it's a phone number or generic ID, mask the middle digits
  if (trimmed.length > 4) {
    return `${trimmed.substring(0, 2)}****${trimmed.substring(trimmed.length - 2)}`;
  }

  return '****';
}
